import React from 'react';
import type {
  BlockElement,
  ButtonElement,
  CheckboxesElement,
  DatepickerElement,
  ImageElement,
  MultiStaticSelectElement,
  OverflowElement,
  PlainTextInputElement,
  RadioButtonsElement,
  StaticSelectElement,
  TimepickerElement,
} from './types';
import { Text } from './mrkdwn';
import { useTokens, useAction } from './theme';

// ─── Button ───────────────────────────────────────────────────────────────────

export function Button({ el }: { el: ButtonElement }) {
  const t = useTokens();
  const onAction = useAction();
  const base: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    borderRadius: 4,
    height: 36,
    padding: '0 16px',
    fontSize: 15,
    fontWeight: 700,
    cursor: 'pointer',
    fontFamily: 'inherit',
    whiteSpace: 'nowrap',
    transition: 'opacity 0.1s',
  };
  const style: React.CSSProperties =
    el.style === 'primary'
      ? { ...base, border: 'none', color: '#fff', background: '#007a5a' }
      : el.style === 'danger'
        ? { ...base, border: 'none', color: '#fff', background: '#e01e5a' }
        : { ...base, border: `1px solid ${t.btnBorder}`, color: t.btnText, background: t.btnBg };

  return (
    <button type="button" style={style} onClick={() => onAction?.(el.action_id ?? '', el.value)}>
      <Text obj={el.text} />
    </button>
  );
}

// ─── Shared input base ────────────────────────────────────────────────────────

function useInputBase(): React.CSSProperties {
  const t = useTokens();
  return {
    display: 'block',
    width: '100%',
    boxSizing: 'border-box',
    border: `1px solid ${t.inputBorder}`,
    borderRadius: 4,
    padding: '7px 10px',
    fontSize: 15,
    lineHeight: '1.5',
    color: t.text,
    background: t.inputBg,
    outline: 'none',
    fontFamily: 'inherit',
  };
}

// ─── PlainTextInput ───────────────────────────────────────────────────────────

export function PlainTextInput({ el }: { el: PlainTextInputElement }) {
  const base = useInputBase();
  const placeholder = el.placeholder?.text ?? '';
  if (el.multiline) {
    return (
      <textarea
        placeholder={placeholder}
        defaultValue={el.initial_value}
        style={{ ...base, resize: 'vertical', minHeight: 80 }}
        rows={4}
      />
    );
  }
  return (
    <input type="text" placeholder={placeholder} defaultValue={el.initial_value} style={base} />
  );
}

// ─── Datepicker ───────────────────────────────────────────────────────────────

export function Datepicker({ el }: { el: DatepickerElement }) {
  const base = useInputBase();
  return <input type="date" defaultValue={el.initial_date} style={{ ...base, width: 'auto' }} />;
}

// ─── Timepicker ───────────────────────────────────────────────────────────────

export function Timepicker({ el }: { el: TimepickerElement }) {
  const base = useInputBase();
  return <input type="time" defaultValue={el.initial_time} style={{ ...base, width: 'auto' }} />;
}

// ─── StaticSelect ─────────────────────────────────────────────────────────────

export function StaticSelect({ el }: { el: StaticSelectElement }) {
  const base = useInputBase();
  return (
    <select style={{ ...base, appearance: 'auto' }}>
      {el.placeholder && (
        <option value="" disabled>
          {el.placeholder.text}
        </option>
      )}
      {el.options.map((o, i) => (
        <option key={i} value={o.value}>
          {o.text.text}
        </option>
      ))}
    </select>
  );
}

// ─── MultiStaticSelect ────────────────────────────────────────────────────────

export function MultiStaticSelect({ el }: { el: MultiStaticSelectElement }) {
  const base = useInputBase();
  return (
    <select multiple style={{ ...base, height: 80 }}>
      {el.options.map((o, i) => (
        <option key={i} value={o.value}>
          {o.text.text}
        </option>
      ))}
    </select>
  );
}

// ─── Overflow ─────────────────────────────────────────────────────────────────

export function Overflow({ el: _el }: { el: OverflowElement }) {
  const t = useTokens();
  return (
    <button
      type="button"
      style={{
        border: `1px solid ${t.btnBorder}`,
        borderRadius: 4,
        padding: '4px 8px',
        background: t.btnBg,
        color: t.textMuted,
        cursor: 'pointer',
        fontSize: 18,
        lineHeight: 1,
      }}
    >
      •••
    </button>
  );
}

// ─── Checkboxes ───────────────────────────────────────────────────────────────

export function Checkboxes({ el }: { el: CheckboxesElement }) {
  const t = useTokens();
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {el.options.map((opt, i) => (
        <label
          key={i}
          style={{ display: 'flex', gap: 8, alignItems: 'flex-start', cursor: 'pointer' }}
        >
          <input
            type="checkbox"
            defaultChecked={el.initial_options?.some((o) => o.value === opt.value)}
            style={{ marginTop: 3, accentColor: '#007a5a' }}
          />
          <div>
            <div style={{ fontSize: 15, color: t.text }}>
              <Text obj={opt.text} />
            </div>
            {opt.description && (
              <div style={{ fontSize: 13, color: t.textMuted, marginTop: 2 }}>
                <Text obj={opt.description} />
              </div>
            )}
          </div>
        </label>
      ))}
    </div>
  );
}

// ─── RadioButtons ─────────────────────────────────────────────────────────────

export function RadioButtons({ el }: { el: RadioButtonsElement }) {
  const t = useTokens();
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {el.options.map((opt, i) => (
        <label
          key={i}
          style={{ display: 'flex', gap: 8, alignItems: 'flex-start', cursor: 'pointer' }}
        >
          <input
            type="radio"
            name={`radio-${i}`}
            defaultChecked={el.initial_option?.value === opt.value}
            style={{ marginTop: 3, accentColor: '#007a5a' }}
          />
          <div>
            <div style={{ fontSize: 15, color: t.text }}>
              <Text obj={opt.text} />
            </div>
            {opt.description && (
              <div style={{ fontSize: 13, color: t.textMuted, marginTop: 2 }}>
                <Text obj={opt.description} />
              </div>
            )}
          </div>
        </label>
      ))}
    </div>
  );
}

// ─── Image ────────────────────────────────────────────────────────────────────

export function ImageEl({ el }: { el: ImageElement }) {
  return <img src={el.image_url} alt={el.alt_text} style={{ maxWidth: '100%', borderRadius: 4 }} />;
}

// ─── Dispatcher ───────────────────────────────────────────────────────────────

export function Element({ el }: { el: BlockElement }) {
  switch (el.type) {
    case 'button':
      return <Button el={el} />;
    case 'plain_text_input':
      return <PlainTextInput el={el} />;
    case 'datepicker':
      return <Datepicker el={el} />;
    case 'timepicker':
      return <Timepicker el={el} />;
    case 'static_select':
      return <StaticSelect el={el} />;
    case 'multi_static_select':
      return <MultiStaticSelect el={el} />;
    case 'overflow':
      return <Overflow el={el} />;
    case 'checkboxes':
      return <Checkboxes el={el} />;
    case 'radio_buttons':
      return <RadioButtons el={el} />;
    case 'image':
      return <ImageEl el={el} />;
    default:
      return null;
  }
}
